import { Table, TableProps, Typography } from "antd";
import { useCelsiusContext } from "../../useCelsiusContext";
import { CSSProperties, useEffect, useMemo, useState } from "react";
import React from "react";
import { formatNumber } from "src/utils/formatting";
import dayjs from "dayjs";
import { useGetPremierPlusBinTapestripData } from "src/queries/useGetPremierPlusBinTapestripData";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import PremierPlusTapestripDTO from "src/models/PremierPlusTapestripDTO";
import './TapestripTable.css';

export function useDayTick() {
  const [dayString, setDayString] = useState(() => dayjs().format('YYYY-MM-DD'));
  useEffect(() => {
    const check = () => {
      let today = dayjs().format('YYYY-MM-DD');
      // if (window.currentDay != null) {
      //   today = window.currentDay as string;
      // }
      // console.log("setting day string to ", today);
      setDayString(prev => (prev !== today ? today : prev));
    };
    // Check every minute — but only update if day changed
    const interval = setInterval(check, 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  return dayString;
}

interface TapestripTableProps {
  inModal: boolean;
  binDto: PremierPlusBinDTO | null | undefined;
  maxTapestrips?: number;
  isReconditioning?: boolean;
}

const { Text } = Typography;

const TapestripTable: React.FC<TapestripTableProps> = React.memo(({ inModal, binDto, maxTapestrips, isReconditioning }) => {
  const { useCelsius } = useCelsiusContext();

  const binId = binDto?.binId ?? null;
  const currentDay = useDayTick();

  // Freeze window once at mount to avoid key churn
  const { startIso, endIso } = useMemo(() => {
    const now = dayjs();
    return {
      startIso: now.subtract(3, "month").startOf('day').toISOString(), // 3 months
      endIso: now.endOf('day').toISOString(),
    };
  }, [currentDay]);

  const { getPremierPlusTapestripDataQuery } = useGetPremierPlusBinTapestripData(
    binId ?? -1,
    startIso,
    endIso,
    {
      enabled: !!binId,
      staleTime: Infinity,          
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const tapestripData = useMemo<PremierPlusTapestripDTO[]>(() => {
    const data = getPremierPlusTapestripDataQuery.data ?? [];
    let modifiedData = [...data].sort(
      (a, b) =>
        new Date(b.captureTimeUtc).getTime() -
        new Date(a.captureTimeUtc).getTime()
    );
    if (isReconditioning != null) {
        modifiedData = modifiedData.filter((t) => (t.isReconditioning === true) === isReconditioning);
    }
    if (maxTapestrips != null) {
        modifiedData = modifiedData.slice(0, maxTapestrips);
    }
    return modifiedData;
  }, [getPremierPlusTapestripDataQuery.data, maxTapestrips, isReconditioning]);

  const tableDataTextStyle = {
    fontFamily: "monospace",
    fontSize: "12px",
    color: "black",
  };

  const columns: TableProps<PremierPlusTapestripDTO>["columns"] = [
    { title: "DateTime", dataIndex: "dateTime", key: "dateTime", width: 128, render: (text) => <p style={{ ...tableDataTextStyle, textAlign: 'center' }}>{dayjs(text).format('MM/DD/YYYY HH:mm')}</p> },
    { title: "Set", dataIndex: "targetMc", key: "setpoint", width: 50, render: () => <p style={{ ...tableDataTextStyle, textAlign: 'center' }}>{binDto?.dryingOperation?.targetMcPercent}</p> },
    { title: "Indiv", dataIndex: "individualMc", key: "indiv", width: 50, render: (text) => <p style={{ ...tableDataTextStyle, textAlign: 'center' }}>{formatNumber(text, {decimalPlaces: 1})}</p> },
    { title: "Avg", dataIndex: "averageMc", key: "avg", width: 50, render: (text) => <p style={{ ...tableDataTextStyle, textAlign: 'center' }}>{formatNumber(text, {decimalPlaces: 1})}</p> },
    { title: "GTemp", dataIndex: "grainTemperatureF", key: "grainTemp", width: 50, render: (text) => <p style={{ ...tableDataTextStyle, textAlign: 'center' }}>{`${formatNumber(text, {decimalPlaces: 1})}${useCelsius ? '°C' : '°F'}`}</p> },
    { title: "Target", dataIndex: "plenumTemperatureSetpointF", key: "target", width: 50, render: (text) => <p style={{ ...tableDataTextStyle, textAlign: 'center' }}>{formatNumber(text, {decimalPlaces: 0})}</p> },
    { title: "Actual", dataIndex: "actualPlenumTemperatureF", key: "actual", width: 50, render: (text) => <p style={{ ...tableDataTextStyle, textAlign: 'center' }}>{`${formatNumber(text, {decimalPlaces: 1})}${useCelsius ? '°C' : '°F'}`}</p> },
    {
      title: "Mach", dataIndex: "isMachineOn", key: "mach", width: 40,
      render: (val) => val === true
        ? <div style={{ display: 'flex', justifyContent: 'center', backgroundColor: '#0420FD' }}>ON</div>
        : <div style={{ display: 'flex', justifyContent: 'center', backgroundColor: '#FD3004' }}>OFF</div>
    },
    { title: "Static", dataIndex: "staticPlenumPressure", key: "static", width: 50, render: (text) => <p style={{ ...tableDataTextStyle, textAlign: 'center' }}>{formatNumber(text, {decimalPlaces: 1})}</p> },
    { title: "Humid", key: "humid", dataIndex: "ambientRh", width: 50, render: (text) => <p style={{ ...tableDataTextStyle, textAlign: 'center' }}>{formatNumber(text, {decimalPlaces: 1})}</p> },
    { title: "Amb", dataIndex: "ambientTemperatureF", key: "amb", width: 50, render: (text) => <p style={{ ...tableDataTextStyle, textAlign: 'center' }}>{`${formatNumber(text, {decimalPlaces: 1})}${useCelsius ? '°C' : '°F'}`}</p> },
    {
      title: "Info", dataIndex: "info", key: "info",
      onCell: () => ({ style: { whiteSpace: 'nowrap', maxWidth: '1000px', width: '100%' } }),
      render: (text) => <p style={tableDataTextStyle}>{text}</p>,
    },
  ];

  const height = inModal ? 500 : 150;
  const paginationHeight = inModal ? 34 : 0; // as measured from devtools
  const tableAndPaginationHeight = height + paginationHeight;
  const tableContainerStyles: CSSProperties = {
  }
  if (inModal) {
    tableContainerStyles.minHeight = tableAndPaginationHeight;
  }

  return (
    <div style={{ display: 'flex', maxWidth: '748px', width: '100%', outline: '1px solid transparent', ...tableContainerStyles }}>
      <Table
        rowKey={(r) =>`${r.captureTimeUtc}`} 
        loading={getPremierPlusTapestripDataQuery.isLoading}
        pagination={inModal ? {
          pageSize: 20,
          position: ['topRight'],
          showSizeChanger: false,
          size: "default",
        } : false}
        columns={columns}
        dataSource={tapestripData}
        size="small"
        bordered
        scroll={{ y: height, x: 1800 }}
        rowClassName={(record) => (record.isMachineOn ? "blue-row" : "red-row")}
        components={{
          header: {
            cell: (props: any) => (
              <td
                {...props}
                style={{
                  padding: "0px",
                  fontSize: "12px",
                  height: "20px",
                  lineHeight: "20px",
                  width: "100%",
                }}
              />
            ),
          },
          body: {
            cell: (props: any) => (
              <td
                {...props}
                style={{
                  padding: 0,
                  margin: 0,
                }}
              />
            ),
          },
        }}
      />
    </div>
  );
});

export default TapestripTable;
